import { Link } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { useSettings, useAddresses } from "@/hooks/usePageData";

export default function Footer() {
  const settings = useSettings();
  const addresses = useAddresses();
  const footerAddresses = addresses.filter(a => a.show_in_footer);

  return (
    <footer data-testid="main-footer" className="bg-[#0A1628] noise-overlay relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-1.5 mb-4">
              <div className="flex flex-col gap-[3px]">
                <div className="w-5 h-[2px] bg-[#C5A059]"></div>
                <div className="w-5 h-[2px] bg-[#C5A059]"></div>
              </div>
              <span className="font-heading text-white text-lg font-medium">Equaliser</span>
              <span className="text-white/50 text-lg font-light">Legal</span>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mt-4">
              {settings?.description || "Corporate legal advisory and outsourced legal desk for businesses in India and abroad."}
            </p>
            <p className="text-[#C5A059]/80 text-xs italic mt-3 font-heading">
              "{settings?.tagline || "Balancing business and law."}"
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-6">Navigation</h4>
            <ul className="space-y-3">
              {[{label:"About",path:"/about"},{label:"Services",path:"/services"},{label:"Sectors",path:"/sectors"},{label:"Team",path:"/team"},{label:"Insights",path:"/insights"},{label:"International",path:"/international"}].map(l => (
                <li key={l.path}><Link to={l.path} data-testid={`footer-${l.label.toLowerCase()}`} className="text-white/60 text-sm hover:text-[#C5A059] transition-colors duration-300">{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-6">Services</h4>
            <ul className="space-y-3">
              {["Corporate & Commercial","Contracts & Negotiations","Compliance & Governance","Disputes Strategy","Outside General Counsel"].map(s => (
                <li key={s}><Link to="/services" className="text-white/60 text-sm hover:text-[#C5A059] transition-colors duration-300">{s}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact - Dynamic addresses */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-6">Offices</h4>
            <ul className="space-y-5">
              {footerAddresses.map(addr => (
                <li key={addr.id} className="flex items-start gap-3">
                  <MapPin size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/70 text-xs font-medium">{addr.office_name}</p>
                    <p className="text-white/50 text-xs">{[addr.city, addr.country].filter(Boolean).join(", ")}</p>
                    {addr.email && <a href={`mailto:${addr.email}`} className="text-white/40 text-xs hover:text-[#C5A059]">{addr.email}</a>}
                  </div>
                </li>
              ))}
              {footerAddresses.length === 0 && (
                <li className="flex items-start gap-3">
                  <MapPin size={14} className="text-[#C5A059] mt-0.5" />
                  <span className="text-white/50 text-xs">Gurugram, Haryana, India</span>
                </li>
              )}
            </ul>
            <div className="mt-5 space-y-2">
              {settings?.primary_email && (
                <a href={`mailto:${settings.primary_email}`} className="flex items-center gap-2 text-white/50 text-xs hover:text-[#C5A059]"><Mail size={12} /> {settings.primary_email}</a>
              )}
              {settings?.primary_phone && (
                <a href={`tel:${settings.primary_phone}`} className="flex items-center gap-2 text-white/50 text-xs hover:text-[#C5A059]"><Phone size={12} /> {settings.primary_phone}</a>
              )}
              {settings?.linkedin_url && (
                <a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" className="flex items-center gap-2 text-white/50 text-xs hover:text-[#C5A059]"><Linkedin size={12} /> LinkedIn</a>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <p className="text-white/30 text-xs leading-relaxed max-w-3xl">
              {settings?.disclaimer || "This website is for informational purposes only and does not constitute legal advice or solicitation. Equaliser Legal LLP and its advocates comply with the rules of the Bar Council of India regarding professional conduct and advertising."}
            </p>
            <p className="text-white/30 text-xs whitespace-nowrap">&copy; {new Date().getFullYear()} {settings?.firm_name || "Equaliser Legal LLP"}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
